// Fechas de la capa de Hechos/Documentos.
//
// Vive suelto —y no adentro de `FactCard`— porque la MISMA fecha se muestra en dos superficies con
// markup distinto: la fila de la cola clásica y la carta del mazo del Recorrido. Duplicar la
// función era duplicar también la trampa que resuelve, y esa clase de duplicación se descubre el
// día que una de las dos muestra un día corrido.

/**
 * Fecha de la fuente (reunión/archivo) de un `ClientDocument`, en formato DD/MM/AAAA.
 *
 * Los dos campos que la alimentan tienen forma distinta, y de ahí sale toda la sutileza:
 *
 * - **`occurred_on` es un día puro** ('AAAA-MM-DD'). Se parte el string y NO se pasa por `Date`:
 *   `new Date('2026-08-07')` se interpreta como medianoche UTC y, formateado en cualquier huso al
 *   oeste, muestra el día ANTERIOR.
 * - **`created_on` es un timestamp con zona** ('2026-09-06T02:38:00Z'), y ahí recortarlo a diez
 *   caracteres devuelve el día **en UTC**: un documento cargado a las 23:38 del 05/09 acá (UTC−3)
 *   se leía «06/09» en el chip de la carta, del modo lista y de «Ruido probable». Por eso el
 *   timestamp sí pasa por `Date`, que es lo único que sabe convertirlo al día local.
 *
 * O sea que las dos reglas son opuestas y las dos son necesarias: la de arriba evita que un día
 * puro retroceda, la de abajo evita que un instante avance.
 *
 * Devuelve `null` si no hay ninguna de las dos, y el valor crudo si no matchea el formato: una
 * fecha rara es mejor información que una fecha inventada.
 */
export function sourceDate(doc) {
    if (!doc) return null;
    const value = String(doc.occurred_on || doc.created_on || '');
    if (!value) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(localDayOf(value));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : (value.slice(0, 10) || null);
}

/**
 * La misma fecha CON la hora local: 'DD/MM/AAAA HH:MM'.
 *
 * Devuelve `null` cuando el valor que manda es un **día puro** (`occurred_on`), porque ahí no hay
 * hora que mostrar y componer una sería inventarla — el que llama cae a `sourceDate`.
 *
 * La usa la sección «Duplicados» sólo cuando los dos hechos de un par caen el mismo día: ahí la
 * fecha no distingue nada y los rótulos «Anterior» / «Más nuevo» quedan sin respaldo visible, justo
 * donde el usuario tiene que decidir cuál es más reciente (QA-VIS-dup-09). Fuera de ese empate no
 * se usa: la hora es ruido en una fila que ya se entiende.
 */
export function sourceDateTime(doc) {
    if (!doc) return null;
    const value = String((doc.occurred_on || doc.created_on) || '');
    if (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()} `
        + `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/** El día 'AAAA-MM-DD' de un valor: tal cual si ya es un día puro, en hora LOCAL si es un instante. */
function localDayOf(value) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
    const pad = (n) => String(n).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

export default sourceDate;
